/**
 * SUBIR UN DOCUMENTO AL ASISTENTE Y DEVOLVERLO RELLENO (28-09-2026).
 *
 * Subir: se lee en el navegador (leer.ts), el ORIGINAL va al bucket privado
 * `assistant-documents` («<empresa>/<id>/<nombre>») y el contenido leído a la fila
 * de `assistant_documents` (RLS: cada persona, los suyos). Si la fila falla, se
 * borra el fichero: no quedan huérfanos.
 *
 * Devolver relleno: la tarjeta `document_fill` trae QUÉ va DÓNDE; aquí se baja el
 * original, se escribe (rellenar.ts) y se entrega con el mismo formato.
 */

import { leerDocumento, huella, resumenDocumento, detectarSeparador, decodificar, type DocumentoLeido, type TipoDocumento } from "./leer";
import { MIME, nombreRelleno, rellenarCsv, rellenarExcel, rellenarWord, type Relleno } from "./rellenar";

export const BUCKET_DOCUMENTOS = "assistant-documents";

/** Lo que el chat necesita de un documento subido (chip y envío). */
export interface DocumentoAdjunto {
  id: string;
  nombre: string;
  tipo: TipoDocumento;
  hojas: number;
  filas: number;
}

const MIME_SUBIDA: Record<TipoDocumento, string> = {
  xlsx: MIME.xlsx,
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
  docx: MIME.docx,
};

/** Un nombre de fichero que el bucket acepta (sin barras ni caracteres raros). */
export function nombreSeguro(nombre: string): string {
  const limpio = nombre.normalize("NFD").replace(/\p{M}+/gu, "").replace(/[^\w.\- ]+/g, "_").replace(/\s+/g, "_").slice(-120);
  return limpio || "documento";
}

type Sb = typeof import("@/integrations/supabase/client").supabase;

export async function subirDocumento(supabase: Sb, companyId: string, file: File): Promise<DocumentoAdjunto> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const leido = await leerDocumento(file.name, bytes);
  const [sha, id] = [await huella(bytes), crypto.randomUUID()];
  const ruta = `${companyId}/${id}/${nombreSeguro(file.name)}`;
  const sub = await supabase.storage.from(BUCKET_DOCUMENTOS).upload(ruta, bytes, { contentType: MIME_SUBIDA[leido.tipo], upsert: false });
  if (sub.error) throw new Error(`subida: ${sub.error.message}`);
  const { error } = await supabase.from("assistant_documents" as never).insert({
    id,
    company_id: companyId,
    file_name: file.name.slice(0, 200),
    kind: leido.tipo,
    size_bytes: bytes.length,
    sha256: sha,
    storage_path: ruta,
    parsed: leido,
  } as never);
  if (error) {
    await supabase.storage.from(BUCKET_DOCUMENTOS).remove([ruta]).catch(() => undefined);
    throw new Error(`fila: ${error.message}`);
  }
  const r = resumenDocumento(leido);
  return { id, nombre: file.name, tipo: leido.tipo, hojas: r.hojas, filas: r.filas };
}

interface FilaDocumento {
  file_name: string;
  kind: TipoDocumento;
  storage_path: string;
  parsed: DocumentoLeido;
}

/** La fila y el ORIGINAL de un documento subido (de quien pregunta: RLS). */
export async function descargarOriginal(supabase: Sb, id: string): Promise<{ fila: FilaDocumento; bytes: Uint8Array }> {
  const { data, error } = await supabase
    .from("assistant_documents" as never)
    .select("file_name, kind, storage_path, parsed")
    .eq("id", id)
    .maybeSingle();
  if (error || !data) throw new Error(`documento: ${error?.message ?? "no encontrado"}`);
  const fila = data as unknown as FilaDocumento;
  const dl = await supabase.storage.from(BUCKET_DOCUMENTOS).download(fila.storage_path);
  if (dl.error || !dl.data) throw new Error(`original: ${dl.error?.message ?? "no encontrado"}`);
  return { fila, bytes: new Uint8Array(await dl.data.arrayBuffer()) };
}

/**
 * El original con el relleno escrito, en su mismo formato. Un .xls (binario viejo)
 * se pasa antes a .xlsx con SheetJS (pierde algo de formato; se avisa con el nombre).
 */
export async function generarRelleno(supabase: Sb, id: string, relleno: Relleno): Promise<{ blob: Blob; nombre: string; mime: string }> {
  const { fila, bytes } = await descargarOriginal(supabase, id);
  const hojas = fila.parsed.hojas;
  if (fila.kind === "docx") {
    const out = await rellenarWord(bytes, hojas, relleno);
    return { blob: new Blob([out as Uint8Array<ArrayBuffer>], { type: MIME.docx }), nombre: nombreRelleno(fila.file_name), mime: MIME.docx };
  }
  if (fila.kind === "csv") {
    const texto = decodificar(bytes);
    const sep = detectarSeparador(texto);
    const hoja = hojas[0];
    const out = rellenarCsv(sep, hoja?.celdas ?? [], relleno);
    return { blob: new Blob(["\uFEFF" + out], { type: "text/csv;charset=utf-8" }), nombre: nombreRelleno(fila.file_name), mime: "text/csv" };
  }
  let xlsx = bytes;
  let nombre = fila.file_name;
  if (fila.kind === "xls") {
    const XLSX = await import("xlsx");
    const wb = XLSX.read(bytes, { type: "array", cellStyles: true });
    xlsx = new Uint8Array(XLSX.write(wb, { type: "array", bookType: "xlsx" }) as ArrayBuffer);
    nombre = nombre.replace(/\.xls$/i, ".xlsx");
  }
  const out = await rellenarExcel(xlsx, hojas, relleno);
  return { blob: new Blob([out as Uint8Array<ArrayBuffer>], { type: MIME.xlsx }), nombre: nombreRelleno(nombre), mime: MIME.xlsx };
}

/**
 * Entrega el fichero: en la app nativa, al visor del sistema (como los informes);
 * si no se puede, y en el navegador, como descarga.
 */
export async function entregarFichero(blob: Blob, nombre: string, mime: string): Promise<void> {
  const [{ isDespiaNative }, { openFileInNativeViewer }] = await Promise.all([import("@/lib/isDespiaNative"), import("@/lib/pdfDelivery")]);
  if (isDespiaNative() && (await openFileInNativeViewer(blob, nombre, mime))) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
